/**
 * Central API path constants for eats-business-web.
 *
 * - Company paths (restaurant, organization, grocery) → company axios instances
 *   (`REACT_APP_API_COMPANY_URL`)
 * - Customer paths → customer API (`REACT_APP_API_CUSTOMER_URL`)
 *
 * Dynamic segments use builder functions: `(id) => \`/resource/${id}\``.
 * Paths marked LEGACY are still referenced in some screens; prefer the canonical
 * route where both are listed.
 */

/** @typedef {string|number} ApiId */

// ---------------------------------------------------------------------------
// Company API (eats-company-api + catering + inventory blueprints)
// ---------------------------------------------------------------------------

export const RESTAURANT_API_PATHS = Object.freeze({
  // System
  protected: '/auth/me',
  authRefresh: '/auth/refresh',
  adsActive: '/ads/active',

  // Auth & signup
  restaurantsRequest: '/restaurant/request',
  restaurantsVerify: '/restaurant/verify',
  restaurantsCheckUserExists: '/restaurant/check-user-exists',
  restaurantsRootAdmin: '/restaurant/root-admin',
  restaurantsLogin: '/restaurant/admin/login',
  restaurantsUserLogin: '/restaurant/users/login',
  restaurantsResetPassword: '/restaurant/reset-password',
  restaurantsRoles: '/restaurant/roles',
  restaurantRootAdminUsers: '/restaurant/root-admin-users',
  restaurantPendingRootUsers: '/restaurant/admin/pending-users',

  // Users
  restaurantsUsers: '/restaurant/users',
  restaurantUserById: (userId) => `/restaurant/users/${userId}`,
  restaurantUsersByRestaurantId: (restaurantId) => `/restaurant/${restaurantId}/users`,
  /** Entity RBAC: global permission catalog for a restaurant. */
  restaurantPermissions: (restaurantId) => `/restaurant/${restaurantId}/permissions`,
  /** Entity RBAC: list/create roles for a restaurant. */
  restaurantRoles: (restaurantId) => `/restaurant/${restaurantId}/roles`,
  restaurantRoleById: (restaurantId, entityRoleId) =>
    `/restaurant/${restaurantId}/roles/${entityRoleId}`,
  restaurantRootAdminByRestaurantId: (restaurantId) => `/restaurant/${restaurantId}/root-admin`,
  restaurantVerifyPassword: (userId) => `/restaurant/verify-password/${userId}`,
  restaurantUserDefault: (userId) => `/restaurant/${userId}/default`,
  restaurantUsersDefault: (userId) => `/restaurant/users/${userId}/default`,
  /** @deprecated Use restaurantsDeliveryAgents — legacy path not registered on company API */
  getUsersByRole: '/get_users_by_role',

  // Restaurants
  restaurants: '/restaurant',
  restaurantById: (restaurantId) => `/restaurant/${restaurantId}`,
  businessProfileMedia: (entityId, entityType) =>
    `/business/${entityId}/profile-media?type=${encodeURIComponent(String(entityType).toLowerCase())}`,
  businessProfileMediaImages: (entityId, entityType) =>
    `/business/${entityId}/profile-media/images?type=${encodeURIComponent(String(entityType).toLowerCase())}`,
  businessProfileMediaYoutube: (entityId, entityType) =>
    `/business/${entityId}/profile-media/youtube?type=${encodeURIComponent(String(entityType).toLowerCase())}`,
  businessProfileMediaSocial: (entityId, entityType) =>
    `/business/${entityId}/profile-media/social?type=${encodeURIComponent(String(entityType).toLowerCase())}`,
  businessProfileMediaTagline: (entityId, entityType) =>
    `/business/${entityId}/profile-media/tagline?type=${encodeURIComponent(String(entityType).toLowerCase())}`,
  businessProfileMediaLogo: (entityId, entityType) =>
    `/business/${entityId}/profile-media/logo?type=${encodeURIComponent(String(entityType).toLowerCase())}`,
  businessProfileMediaById: (entityId, entityType, mediaId) =>
    `/business/${entityId}/profile-media/${mediaId}?type=${encodeURIComponent(String(entityType).toLowerCase())}`,
  businessPublicProfile: (entityId, entityType) =>
    `/business/${entityId}/profile?type=${encodeURIComponent(String(entityType).toLowerCase())}`,
  restaurantProfileMedia: (restaurantId) => `/restaurant/${restaurantId}/profile-media`,
  restaurantProfileMediaImages: (restaurantId) =>
    `/restaurant/${restaurantId}/profile-media/images`,
  restaurantProfileMediaYoutube: (restaurantId) =>
    `/restaurant/${restaurantId}/profile-media/youtube`,
  restaurantProfileMediaById: (restaurantId, mediaId) =>
    `/restaurant/${restaurantId}/profile-media/${mediaId}`,
  restaurantPublicProfile: (restaurantId) => `/restaurant/${restaurantId}/public-profile`,
  restaurantProperties: (restaurantId) => `/restaurant/${restaurantId}/properties`,
  restaurantOrderNumberConfig: (restaurantId) =>
    `/restaurant/${restaurantId}/order-number-config`,
  restaurantNearbyGroceryStores: (restaurantId) =>
    `/restaurant/${restaurantId}/nearby-grocery-stores`,
  restaurantGroceryOrders: (restaurantId) =>
    `/restaurant/${restaurantId}/groceries/orders`,
  restaurantGroceryOrderById: (restaurantId, groceryOrderId) =>
    `/restaurant/${restaurantId}/groceries/orders/${groceryOrderId}`,
  restaurantGroceryMasterOrderById: (restaurantId, masterOrderId) =>
    `/restaurant/${restaurantId}/groceries/master-orders/${masterOrderId}`,
  restaurantGroceryRazorpayOrder: (restaurantId) =>
    `/restaurant/${restaurantId}/groceries/payments/razorpay-order`,
  restaurantGroceryPaymentSuccess: (restaurantId) =>
    `/restaurant/${restaurantId}/groceries/payments/success`,
  restaurantDeactivate: (restaurantId) => `/restaurant/${restaurantId}/deactivate`,
  restaurantJreatsMealDeliveryTimes: (restaurantId) =>
    `/restaurant/${restaurantId}/jreats/meal-delivery-times`,
  restaurantMyeatsOrgMealDeliveryTimes: (restaurantId) =>
    `/restaurant/${restaurantId}/myeats/org-meal-delivery-times`,
  restaurantMyeatsDeliverySlots: (restaurantId) =>
    `/restaurant/${restaurantId}/myeats/delivery-slots`,
  restaurantMyeatsOffices: (restaurantId) =>
    `/restaurant/${restaurantId}/myeats/offices`,
  restaurantMyeatsOfficesSearch: (restaurantId) =>
    `/restaurant/${restaurantId}/myeats/offices/search`,
  restaurantMyeatsOfficeByOrg: (restaurantId, organizationId) =>
    `/restaurant/${restaurantId}/myeats/offices/${organizationId}`,
  restaurantCateringFarmhouses: (restaurantId) =>
    `/restaurant/${restaurantId}/catering/farmhouses`,
  restaurantCateringFarmhousesSearch: (restaurantId) =>
    `/restaurant/${restaurantId}/catering/farmhouses/search`,
  restaurantCateringFarmhouseById: (restaurantId, partyHallId) =>
    `/restaurant/${restaurantId}/catering/farmhouses/${partyHallId}`,
  restaurantJreatsSchools: (restaurantId) =>
    `/restaurant/${restaurantId}/jreats/schools`,
  restaurantJreatsSchoolsSearch: (restaurantId) =>
    `/restaurant/${restaurantId}/jreats/schools/search`,
  restaurantJreatsSchoolByOrg: (restaurantId, organizationId) =>
    `/restaurant/${restaurantId}/jreats/schools/${organizationId}`,
  jreatsPlatformFee: '/jreats/platform-fee',
  jreatsMainMenu: '/eats/jreats/main-menu',
  jreatsMainMenuStates: '/eats/jreats/main-menu/states',
  jreatsMainMenuById: (foodItemId) => `/eats/jreats/main-menu/${foodItemId}`,

  /** Global menu master (platform catalog for MyEats / Dine-In adopt). */
  menuMaster: '/eats/menu-master',
  menuMasterById: (masterId) => `/eats/menu-master/${masterId}`,
  restaurantMenuMaster: '/restaurant/menu-master',
  restaurantFoodItemsFromMaster: (restaurantId) =>
    `/restaurant/${restaurantId}/food-items/from-master`,
  companyTaxes: '/company-taxes',
  companyTaxById: (taxId) => `/company-taxes/${taxId}`,
  pickupPricing: '/pickup-pricing',
  pickupHubs: '/pickup-hubs',
  pickupHubsBootstrap: '/pickup-hubs/bootstrap',
  pickupHubById: (hubId) => `/pickup-hubs/${hubId}`,
  pickupHubTransfers: (hubId) => `/pickup-hubs/${hubId}/transfers`,
  pickupHubSchools: (hubId) => `/pickup-hubs/${hubId}/schools`,
  pickupHubSchoolByOrg: (hubId, organizationId) =>
    `/pickup-hubs/${hubId}/schools/${organizationId}`,
  pickupHubPricing: (hubId) => `/pickup-hubs/${hubId}/pricing`,
  pickupLunchboxQuote: '/pickup-lunchbox/quote',
  restaurantServices: (restaurantId) => `/restaurant/${restaurantId}/services`,
  restaurantServiceById: (serviceId) => `/restaurant/services/${serviceId}`,
  restaurantSearch: '/restaurant/search',
  /** Restaurants linked to user via RestaurantUserRoleLT (all assigned). */
  userRestaurantIds: (userId) => `/user/${userId}/restaurant`,
  /** Restaurants created by user. */
  userRestaurants: (userId) => `/user/${userId}/restaurants`,
  userDefaultLegacy: (userId) => `/default/${userId}`,

  // Addresses (restaurant + Places autocomplete)
  restaurantAddressById: (addressId) => `/restaurant/addresses/${addressId}`,
  addressSuggest: '/address/suggest',
  addressSuggestDetails: '/address/suggest/details',
  addressesSuggestions: '/address/suggest',
  addressesDetails: '/address/suggest/details',

  // Menu — food items & categories
  restaurantsIngredients: '/restaurant/ingredients',
  restaurantsFoodCategories: '/restaurant/food-categories',
  restaurantsFoodItems: '/restaurant/food-items',
  restaurantFoodItemsByRestaurantId: (restaurantId) =>
    `/restaurant/${restaurantId}/food-items`,
  restaurantMyeatsFoodItemsById: (restaurantId) =>
    `/restaurant/${restaurantId}/myeats/food-items`,
  restaurantFoodItemById: (foodItemId) => `/restaurant/food-items/${foodItemId}`,
  restaurantFoodItemAgePricingById: (foodItemId) =>
    `/restaurant/food-items/${foodItemId}/age-pricing`,
  restaurantMyeatsSearch: '/restaurant/myeats/search',
  restaurantMyeatsSearchFood: '/restaurant/myeats/search-food',
  deleteFoodItemById: (foodItemId) => `/restaurant/food-items/${foodItemId}`,
  /** @deprecated Use deleteFoodItemById */
  deleteFoodItemLegacy: (foodItemId) => `/restaurant/food-items/${foodItemId}`,

  // Menu — daily items
  restaurantsDailyMenuItems: '/restaurant/daily-menu-items',
  restaurantDailyMenuItemsByRestaurantId: (restaurantId) =>
    `/restaurant/${restaurantId}/daily-menu-items`,
  restaurantDailyMenuItemById: (dailyItemId) => `/restaurant/daily-menu-items/${dailyItemId}`,
  /** @deprecated Use restaurantsDailyMenuItems or restaurantDailyMenuItemById */
  addDailyMenuItemLegacy: '/restaurant/daily-menu-items',
  updateDailyMenuItemLegacy: (dailyItemId) => `/restaurant/daily-menu-items/${dailyItemId}`,
  deleteDailyMenuItemLegacy: (dailyItemId) => `/restaurant/daily-menu-items/${dailyItemId}`,
  restaurantDailyMenuAutoPublish: '/restaurant/daily-menu/auto-publish',

  // Menu — templates
  restaurantsMenuTemplates: '/restaurant/menu-templates',
  restaurantMenuTemplatesByRestaurantId: (restaurantId) =>
    `/restaurant/${restaurantId}/menu-templates`,
  restaurantMenuTemplateById: (templateId) => `/restaurant/menu-templates/${templateId}`,
  restaurantMenuTemplateItems: (templateId) =>
    `/restaurant/menu-templates/${templateId}/items`,
  restaurantMenuTemplateItemById: (tmpItemId) =>
    `/restaurant/menu-templates/items/${tmpItemId}`,

  // Recipes
  restaurantsFoodItemsRecipes: '/restaurant/food-items/recipes',

  // Orders & payments
  restaurantsOrders: '/restaurant/orders',
  restaurantOrdersByRestaurantId: (restaurantId) => `/restaurant/${restaurantId}/orders`,
  restaurantOrderHistoryByRestaurantId: (restaurantId) =>
    `/restaurant/${restaurantId}/order-history`,
  restaurantOrderById: (orderId) => `/restaurant/orders/${orderId}`,
  restaurantDineInOrderById: (orderId) => `/restaurant/dine-in/orders/${orderId}`,
  restaurantCancelOrder: (orderId) => `/restaurant/cancel/${orderId}`,
  restaurantsPayments: '/restaurant/payments',
  restaurantPaymentsByRestaurantId: (restaurantId) =>
    `/restaurant/${restaurantId}/payments`,
  updateOrderLegacy: (orderId) => `/restaurant/orders/${orderId}`,

  // Partner settlements (platform → restaurant)
  restaurantSettlementsPayableSummary: (restaurantId) =>
    `/restaurant/${restaurantId}/settlements/payable-summary`,
  restaurantSubscriptionSettlementsSummary: (restaurantId) =>
    `/restaurant/${restaurantId}/subscription-settlements/summary`,
  restaurantSubscriptionSettlementsUnsettled: (restaurantId) =>
    `/restaurant/${restaurantId}/subscription-settlements/unsettled`,
  restaurantSubscriptionSettlementBatches: (restaurantId) =>
    `/restaurant/${restaurantId}/subscription-settlements/batches`,
  restaurantSubscriptionSettlementBatchById: (batchId) =>
    `/restaurant/subscription-settlements/batches/${batchId}`,
  restaurantSubscriptionSettlementBatchMarkPaid: (batchId) =>
    `/restaurant/subscription-settlements/batches/${batchId}/mark-paid`,
  restaurantSubscriptionSettlementBatchCancel: (batchId) =>
    `/restaurant/subscription-settlements/batches/${batchId}/cancel`,
  restaurantOrderSettlementsSummary: (restaurantId) =>
    `/restaurant/${restaurantId}/order-settlements/summary`,
  restaurantOrderSettlementsUnsettled: (restaurantId) =>
    `/restaurant/${restaurantId}/order-settlements/unsettled`,
  restaurantOrderSettlementBatches: (restaurantId) =>
    `/restaurant/${restaurantId}/order-settlements/batches`,
  restaurantOrderSettlementBatchById: (batchId) =>
    `/restaurant/order-settlements/batches/${batchId}`,
  restaurantOrderSettlementBatchMarkPaid: (batchId) =>
    `/restaurant/order-settlements/batches/${batchId}/mark-paid`,
  restaurantOrderSettlementBatchCancel: (batchId) =>
    `/restaurant/order-settlements/batches/${batchId}/cancel`,

  // Pickup orders
  restaurantPickupOrdersByRestaurantId: (restaurantId) =>
    `/restaurant/${restaurantId}/pickup-orders`,
  pickupOrdersLegacy: (restaurantId) => `/restaurant/${restaurantId}/pickup-orders`,
  acceptOrderLegacy: (orderId) => `/accept_order/${orderId}`,

  // Delivery agents
  restaurantsDeliveryAgents: '/restaurant/delivery-agents',
  restaurantDeliveryAgentOrders: (userId) =>
    `/restaurant/delivery-agents/${userId}/orders`,
  deliveryAgentOrdersLegacy: (userId) => `/delivery-agent/orders/${userId}`,

  // Catering
  cateringMenuByRestaurantId: (restaurantId) => `/catering/menu/${restaurantId}`,
  cateringMenu: '/catering/menu',
  cateringMenuItemById: (itemId) => `/catering/menu-item/${itemId}`,
  cateringMenuItemsDelete: (itemId) => `/catering/menu-items/${itemId}`,
  cateringTempByRestaurantId: (restaurantId) => `/catering/temp/${restaurantId}`,
  cateringTemp: '/catering/temp',
  cateringTempItemById: (tempId) => `/catering/temp-item/${tempId}`,
  cateringTempItemsDelete: (tempId) => `/catering/temp-items/${tempId}`,
  cateringTempPackageItems: '/catering/temp-package-items',
  cateringTempPackageItemById: (lineId) => `/catering/temp-package-item/${lineId}`,
  cateringOrder: '/catering/order',
  cateringOrderById: (orderId) => `/catering/order/${orderId}`,
  cateringCancelOrder: (orderId) => `/catering/cancel-order/${orderId}`,
  cateringOrdersByRestaurantId: (restaurantId) => `/catering/orders/${restaurantId}`,
  cateringQuotesByRestaurantId: (restaurantId) => `/catering/quotes/${restaurantId}`,
  cateringQuotesCreate: '/catering/quotes',
  cateringQuoteById: (quoteId) => `/catering/quote/${quoteId}`,
  cateringPaymentsByRestaurantId: (restaurantId) => `/catering/payments/${restaurantId}`,
  cateringPayment: '/catering/payment',
  partyHallQuotes: '/party-hall/quotes',
  partyHallQuotesByRestaurantId: (restaurantId) => `/party-hall/quotes/${restaurantId}`,
  partyHallQuoteById: (quoteId) => `/party-hall/quote/${quoteId}`,
  partyHallsByRestaurantId: (restaurantId) => `/party-hall/halls/${restaurantId}`,
  partyHallImageUpload: '/party-hall/images',
  partyHallImagesByHallId: (partyHallId) => `/party-hall/${partyHallId}/images`,
  partyHallImageById: (imageId) => `/party-hall/images/${imageId}`,
  /** LEGACY — prefer cateringOrderById / cateringOrder */
  cateringOrderLegacy: (orderId) => `/catering/order/${orderId}`,
  cateringOrdersLegacy: (restaurantId) => `/catering/orders/${restaurantId}`,
  updateCateringOrderLegacy: (orderId) => `/catering/order/${orderId}`,
  cancelCateringOrderLegacy: (orderId) => `/catering/cancel-order/${orderId}`,

  // Inventory & suppliers
  groceries: '/groceries',
  restaurantInventoryItem: '/restaurant/inventory/item',
  /** @deprecated Use restaurantInventoryItemById */
  inventoryItemLegacy: (inventoryItemId) => `/restaurant/inventory-item/${inventoryItemId}`,
  restaurantInventoryItemsByRestaurantId: (restaurantId) =>
    `/restaurant/${restaurantId}/inventory-items`,
  restaurantInventoryItemById: (inventoryItemId) =>
    `/restaurant/inventory-item/${inventoryItemId}`,
  restaurantSuppliersByRestaurantId: (restaurantId) =>
    `/restaurant/${restaurantId}/suppliers`,
  restaurantSupplierById: (supplierId) => `/restaurant/suppliers/${supplierId}`,
  restaurantsSuppliers: '/restaurant/suppliers',
  restaurantInventoryPurchasesByRestaurantId: (restaurantId) =>
    `/restaurant/${restaurantId}/inventory-purchases`,
  restaurantInventoryPurchaseById: (purchaseId) =>
    `/restaurant/inventory-purchases/${purchaseId}`,
  restaurantInventoryPurchaseAttachments: (purchaseId) =>
    `/restaurant/inventory-purchases/${purchaseId}/attachments`,
  restaurantsInventoryPurchases: '/restaurant/inventory-purchases',

  // Subscriptions (restaurant billing)
  restaurantsSubscriptions: '/restaurant/subscriptions',
  restaurantSubscriptionsByRestaurantId: (restaurantId) =>
    `/restaurant/${restaurantId}/subscriptions`,
  restaurantSubscriptionByPaymentId: (paymentId) =>
    `/restaurant/subscriptions/${paymentId}`,

  // Job profiles (hiring)
  jobProfiles: '/job-profiles',
});

// ---------------------------------------------------------------------------
// Organization API (separate organization service)
// ---------------------------------------------------------------------------

export const ORGANIZATION_API_PATHS = Object.freeze({
  // Eats business employees (EAP)
  eatsBusinessUsers: '/eats-business/users',
  eatsBusinessUser: (userId) => `/eats-business/users/${userId}`,

  // Eats admin views over customer data
  adminCustomerSearch: '/admin/customers/search',
  adminOrganizations: '/admin/organizations',
  adminFoodSubscriptions: '/admin/food-subscriptions',
  adminPickupSubscriptions: '/admin/pickup-subscriptions',

  // Unified partner login (company API)
  partnerLogin: '/partner/login',
  partnerForgotPassword: '/partner/password/forgot',
  partnerVerifyResetOtp: '/partner/password/verify-otp',
  partnerResetPassword: '/partner/password/reset',
  partnerContexts: '/partner/contexts',
  partnerSwitchContext: '/partner/switch-context',
  partnerFcmToken: '/partner/fcm-token',

  // Auth
  organizationsAdminLogin: '/organization/admin/login',
  organizationsUsersLogin: '/organization/users/login',
  organizationsPasswordReset: '/organization/password/reset',
  passwordChange: '/organization/password/change',
  request: '/organization/otp/request',
  verify: '/organization/otp/verify',

  // Signup / OTP
  checkOrgUserExists: '/organization/check-user-exists',
  otpRequest: '/organization/otp/request',
  otpVerify: '/organization/otp/verify',
  rootUser: '/organization/root-user',
  organizationRootAdminUsers: '/organization/root-admin-users',

  // Organization CRUD
  organization: '/organization',
  organizationById: (organizationId) => `/organization/${organizationId}`,
  organizationRootAdminByOrganizationId: (organizationId) =>
    `/organization/${organizationId}/root-admin`,
  organizationAddress: '/organization/address',
  organizationAddressByOrgId: (organizationId) =>
    `/organization/address/${organizationId}`,
  organizationAddressCreateLegacy: (organizationId) =>
    `/organization_address/${organizationId}`,

  // Features
  organizationFeaturesByOrgId: (organizationId) =>
    `/organization/${organizationId}/features`,
  organizationFeatureById: (featureId) => `/organization/features/${featureId}`,

  // Contacts & users
  organizationUser: '/organization/users',
  organizationUsersByOrgId: (organizationId) =>
    `/organization/${organizationId}/users`,
  organizationUserById: (organizationUserId) =>
    `/organization/users/${organizationUserId}`,
  userById: (userId) => `/organization/user/${userId}`,

  // Orders & media
  organizationOrdersByOrgId: (organizationId) =>
    `/organization/${organizationId}/orders`,
  organizationImagesByOrgId: (organizationId) =>
    `/organization/${organizationId}/images`,
  organizationImageUpload: '/organization/images',
  organizationImageById: (imageId) => `/organization/images/${imageId}`,

  // Address autocomplete (organization service)
  getAddress: '/address/suggest',
  getAddressDetails: '/address/suggest/details',
});

// ---------------------------------------------------------------------------
// Grocery API (inventory product catalog)
// ---------------------------------------------------------------------------

export const GROCERY_API_PATHS = Object.freeze({
  units: '/groceries/units',
  categories: '/groceries/categories',
  brands: '/groceries/brands',
  supermarkets: '/groceries/supermarkets',
  /** Active stores linked to user via company_user_role_lt (STR). */
  storesByUserId: (userId) => `/groceries/stores/${userId}`,
  /** GET list by store id; POST create; PUT/DELETE by inventory item id. */
  groceryItems: '/groceries/items',
  groceryItemById: (itemId) => `/groceries/items/${itemId}`,
  supermarketItems: (supermarketId) => `/groceries/items/${supermarketId}`,
  groceryOrders: '/groceries/orders',
  groceryMasterOrders: '/groceries/master-orders',
  groceryMasterOrderById: (masterOrderId) => `/groceries/master-orders/${masterOrderId}`,
  groceryOrdersByStoreId: (storeId) => `/groceries/store/${storeId}/orders`,
  groceryOrderHistoryByStoreId: (storeId) => `/groceries/store/${storeId}/order-history`,
  groceryOrderById: (orderId) => `/groceries/orders/${orderId}`,
  groceryOrderStatus: (orderId) => `/groceries/orders/${orderId}/status`,
  groceryOrderPayments: (orderId) => `/groceries/orders/${orderId}/payments`,
  stores: '/groceries/stores',
  storeById: (storeId) => `/groceries/store/${storeId}`,
  storeMyeatsDeliverySlots: (storeId) => `/groceries/store/${storeId}/myeats/delivery-slots`,
  storeProfileMedia: (storeId) => `/groceries/store/${storeId}/profile-media`,
  storeProfileMediaImages: (storeId) => `/groceries/store/${storeId}/profile-media/images`,
  storeProfileMediaYoutube: (storeId) => `/groceries/store/${storeId}/profile-media/youtube`,
  storeProfileMediaById: (storeId, mediaId) =>
    `/groceries/store/${storeId}/profile-media/${mediaId}`,
  storePublicProfile: (storeId) => `/groceries/store/${storeId}/public-profile`,
  storeTimings: (storeId) => `/groceries/store/${storeId}/timings`,
  storeOrderNumberConfig: (storeId) => `/groceries/store/${storeId}/order-number-config`,
  storeRootAdminByStoreId: (storeId) => `/groceries/store/${storeId}/root-admin`,
  storeByIdUpdate: (storeId) => `/groceries/stores/${storeId}`,
  storeAddressById: (addressId) => `/groceries/store-address/${addressId}`,
  storeUsers: '/groceries/stores/users',
  storeUsersByStoreId: (storeId) => `/groceries/stores/${storeId}/users`,
  /** Entity RBAC: global permission catalog for a store. */
  storePermissions: (storeId) => `/groceries/store/${storeId}/permissions`,
  /** Entity RBAC: list/create roles for a store. */
  storeRoles: (storeId) => `/groceries/store/${storeId}/roles`,
  storeRoleById: (storeId, entityRoleId) =>
    `/groceries/store/${storeId}/roles/${entityRoleId}`,
  pendingStores: '/groceries/pending-stores',
  storeRootAdminUsers: '/groceries/root-admin-users',
  storeSuppliersByStoreId: (storeId) => `/groceries/store/${storeId}/suppliers`,
  storeInventoryPurchasesByStoreId: (storeId) =>
    `/groceries/store/${storeId}/inventory-purchases`,
  storeInventoryItemsByStoreId: (storeId) =>
    `/groceries/store/${storeId}/inventory-items`,
  storeInventoryItemById: (itemId) => `/groceries/store/inventory-items/${itemId}`,
  storeSuppliers: '/groceries/store/suppliers',
  storeSupplierById: (supplierId) => `/groceries/store/suppliers/${supplierId}`,
  storeInventoryPurchases: '/groceries/store/inventory-purchases',
  storeInventoryPurchaseById: (purchaseId) =>
    `/groceries/store/inventory-purchases/${purchaseId}`,
  storeInventoryPurchaseAttachments: (purchaseId) =>
    `/groceries/store/inventory-purchases/${purchaseId}/attachments`,
  /** Store-as-buyer: nearby seller stores within radiusKm (default 5). */
  storeNearbyGroceryStores: (storeId) =>
    `/groceries/store/${storeId}/nearby-grocery-stores`,
  storeGroceryOrders: (storeId) => `/groceries/store/${storeId}/groceries/orders`,
  storeGroceryRazorpayOrder: (storeId) =>
    `/groceries/store/${storeId}/groceries/payments/razorpay-order`,
  storeGroceryPaymentSuccess: (storeId) =>
    `/groceries/store/${storeId}/groceries/payments/success`,
});

// ---------------------------------------------------------------------------
// Customer API (eats-customer-api — Eats Business login / auth)
// ---------------------------------------------------------------------------

export const CUSTOMER_API_PATHS = Object.freeze({
  eatsBusinessLogin: '/eats-business/login',
  /** @deprecated Prefer eatsBusinessLogin */
  eatsAppAdminLogin: '/eats-business/login',
  /** @deprecated Prefer eatsBusinessLogin */
  eatsAppUsersLogin: '/eats-business/login',
  eatsAppAuthMe: '/eats-business/auth/me',
  eatsAppAuthRefresh: '/eats-business/auth/refresh',
  appUsers: '/app/users',
  customerJreatsFullMenu: '/customer/jreats/full-menu',
  customerJreatsFullMenuStates: '/customer/jreats/full-menu/states',
  customerRestaurantProfile: (restaurantId) => `/customer/restaurant/${restaurantId}/profile`,
  customerStoreProfile: (storeId) => `/customer/groceries/store/${storeId}/profile`,
  adminFoodSubscriptions: '/admin/food-subscriptions',
  adminPickupSubscriptions: '/admin/pickup-subscriptions',
});

// ---------------------------------------------------------------------------
// Combined export (default import surface)
// ---------------------------------------------------------------------------

export const API_PATHS = Object.freeze({
  ...RESTAURANT_API_PATHS,
  ...ORGANIZATION_API_PATHS,
  ...GROCERY_API_PATHS,
  ...CUSTOMER_API_PATHS,
});

export default API_PATHS;
